import React, { useState, useEffect } from 'react';
import { responseHero } from '../../../utils/axiosInterceptor';
import { toast } from 'react-toastify';
import { formatCrypto } from '../../../utils/formatCrypto';
import { formatCurrency } from '../utils/formatCurrency';

interface FixRealizationModalProps {
    isOpen: boolean;
    onClose: () => void;
    onSuccess: () => void;
    maxBtc: number;
    maxEth: number;
    marketPrices: { btc: string | number; eth: string | number } | null;
}

export const FixRealizationModal: React.FC<FixRealizationModalProps> = ({
    isOpen,
    onClose,
    onSuccess,
    maxBtc,
    maxEth,
    marketPrices
}) => {
    const [assetType, setAssetType] = useState<number>(1); // 1 = BTC, 2 = ETH
    const [quantity, setQuantity] = useState<string>('');
    const [exchangeRate, setExchangeRate] = useState<string>('');
    const [note, setNote] = useState<string>('');
    const [submitting, setSubmitting] = useState<boolean>(false);

    const isBtc = assetType === 1;
    const maxAvailable = isBtc ? maxBtc : maxEth;
    const assetTicker = isBtc ? 'BTC' : 'ETH';

    // Set initial exchange rate based on current market price when modal opens or asset changes
    useEffect(() => {
        if (marketPrices) {
            const currentPrice = isBtc ? marketPrices.btc : marketPrices.eth;
            setExchangeRate(Number(currentPrice).toFixed(2));
        }
    }, [assetType, marketPrices, isBtc]);

    if (!isOpen) return null;

    const handleSetMax = () => {
        setQuantity(maxAvailable.toString());
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        
        const numQty = parseFloat(quantity);
        const numRate = parseFloat(exchangeRate);

        if (isNaN(numQty) || numQty <= 0) {
            toast.error("Kérjük, adjon meg egy érvényes mennyiséget!");
            return;
        }
        if (numQty > maxAvailable) {
            toast.error(`Nincs elegendő fedezet! Maximálisan realizálható: ${maxAvailable} ${assetTicker}`);
            return;
        }
        if (isNaN(numRate) || numRate <= 0) {
            toast.error("Kérjük, adjon meg egy érvényes árfolyamot!");
            return;
        }

        setSubmitting(true);
        try {
            await responseHero.post(`${process.env.REACT_APP_API_URL}/bmp/wallet/fixed-realization`, {
                data: {
                    assetType,
                    totalQuantityToSell: quantity,
                    exchangeRate,
                    note: note.trim() || undefined
                }
            });

            toast.success("Sikeres fix realizációs folyamat elindítva!");
            setQuantity('');
            setNote('');
            onSuccess();
        } catch (error: any) {
            console.error("Hiba a fix realizáció során:", error);
            toast.error(error?.response?.data?.message || "Hiba történt a realizáció feldolgozásakor.");
        } finally {
            setSubmitting(false);
        }
    };

    // Calculate approximate estimated value output
    const estimatedValue = !isNaN(parseFloat(quantity)) && !isNaN(parseFloat(exchangeRate))
        ? parseFloat(quantity) * parseFloat(exchangeRate)
        : 0;

    return (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-sm">
            <div className="bg-white rounded-[2rem] shadow-2xl w-full max-w-md overflow-hidden animate-in zoom-in-95 duration-200">
                
                {/* Header */}
                <div className="p-6 border-b border-slate-50 flex justify-between items-center bg-amber-50/40">
                    <div>
                        <h3 className="text-lg font-black text-slate-800 tracking-tight">Fix Eszköz Realizálása</h3>
                        <p className="text-[10px] font-bold text-amber-600 uppercase tracking-wider">FIX Áthelyezés → TRADE & Likviditás eladás</p>
                    </div>
                    <button 
                        type="button" 
                        onClick={onClose} 
                        className="text-slate-400 hover:text-slate-600 text-2xl transition-colors leading-none"
                        disabled={submitting}
                    >
                        &times;
                    </button>
                </div>

                <form onSubmit={handleSubmit} className="p-6 space-y-4">
                    {/* Asset Type Selector Tab toggles */}
                    <div>
                        <label className="block text-[9px] font-black uppercase text-slate-400 mb-1.5 tracking-wider">
                            Kiválasztott Eszköz
                        </label>
                        <div className="grid grid-cols-2 gap-2 p-1 bg-slate-100 rounded-xl">
                            <button
                                type="button"
                                onClick={() => { setAssetType(1); setQuantity(''); }}
                                className={`py-2 text-xs font-black rounded-lg transition-all ${assetType === 1 ? 'bg-white text-orange-600 shadow-sm' : 'text-slate-500 hover:text-slate-800'}`}
                            >
                                Bitcoin (BTC)
                            </button>
                            <button
                                type="button"
                                onClick={() => { setAssetType(2); setQuantity(''); }}
                                className={`py-2 text-xs font-black rounded-lg transition-all ${assetType === 2 ? 'bg-white text-indigo-600 shadow-sm' : 'text-slate-500 hover:text-slate-800'}`}
                            >
                                Ethereum (ETH)
                            </button>
                        </div>
                    </div>

                    {/* Quantity Input with Max option */}
                    <div>
                        <div className="flex justify-between items-center mb-1">
                            <label className="block text-[9px] font-black uppercase text-slate-400 tracking-wider">
                                Eladni kívánt mennyiség
                            </label>
                            <span className="text-[10px] font-bold text-slate-400">
                                Elérhető: <strong className="text-slate-700">{formatCrypto(maxAvailable)}</strong>
                            </span>
                        </div>
                        <div className="relative">
                            <input
                                type="number"
                                step="any"
                                required
                                min="0"
                                max={maxAvailable}
                                className="w-full bg-white border border-slate-200 focus:border-amber-500 rounded-xl pl-3 pr-16 py-2.5 text-xs font-black text-slate-800 outline-none focus:ring-2 focus:ring-amber-500/10 transition-all font-mono"
                                placeholder="0.00000000"
                                value={quantity}
                                onChange={(e) => setQuantity(e.target.value)}
                                disabled={submitting}
                            />
                            <button
                                type="button"
                                onClick={handleSetMax}
                                className="absolute right-2 top-1/2 -translate-y-1/2 px-2 py-1 bg-amber-100 hover:bg-amber-200 text-amber-800 font-bold text-[9px] uppercase rounded-md transition-colors"
                            >
                                MAX
                            </button>
                        </div>
                    </div>

                    {/* Exchange Rate Input */}
                    <div>
                        <label className="block text-[9px] font-black uppercase text-slate-400 mb-1 tracking-wider">
                            Eladási Árfolyam (USD)
                        </label>
                        <input
                            type="number"
                            step="any"
                            required
                            className="w-full bg-white border border-slate-200 focus:border-amber-500 rounded-xl px-3 py-2.5 text-xs font-black text-slate-800 outline-none focus:ring-2 focus:ring-amber-500/10 transition-all font-mono"
                            placeholder="0.00"
                            value={exchangeRate}
                            onChange={(e) => setExchangeRate(e.target.value)}
                            disabled={submitting}
                        />
                    </div>

                    {/* Text Note area */}
                    <div>
                        <label className="block text-[9px] font-black uppercase text-slate-400 mb-1 tracking-wider">
                            Megjegyzés / Indoklás
                        </label>
                        <textarea
                            className="w-full bg-white border border-slate-200 focus:border-amber-500 rounded-xl px-3 py-2 text-xs font-medium text-slate-800 outline-none focus:ring-2 focus:ring-amber-500/10 transition-all resize-none min-h-[60px]"
                            placeholder="Pl.: Részleges profit kivétel elérése..."
                            value={note}
                            onChange={(e) => setNote(e.target.value)}
                            disabled={submitting}
                        />
                    </div>

                    {/* Value Projection Banner */}
                    {estimatedValue > 0 && (
                        <div className="bg-emerald-50 border border-emerald-100 rounded-xl p-3 flex justify-between items-center text-xs">
                            <span className="font-bold text-emerald-800">Várhatóan befolyó likviditás:</span>
                            <span className="font-black text-emerald-600">{formatCurrency(estimatedValue)}</span>
                        </div>
                    )}

                    {/* Action Panel Buttons */}
                    <div className="flex gap-3 pt-2">
                        <button
                            type="button"
                            onClick={onClose}
                            className="w-1/3 py-3 rounded-xl font-bold text-xs uppercase text-slate-500 hover:bg-slate-50 border border-slate-100 transition-all"
                            disabled={submitting}
                        >
                            Mégse
                        </button>
                        <button
                            type="submit"
                            className="w-2/3 py-3 rounded-xl font-black text-xs uppercase tracking-wider text-white bg-amber-600 hover:bg-amber-700 disabled:opacity-40 transition-all shadow-md shadow-amber-600/10 active:scale-98"
                            disabled={submitting || parseFloat(quantity) <= 0 || parseFloat(quantity) > maxAvailable}
                        >
                            {submitting ? 'Feldolgozás...' : 'Realizáció Indítása'}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};